/**
 * Deduplicate GET /api/users/me across the app (Login, SessionSync, ProtectedRoute, etc.).
 * React 19 / strict effects and router updates can fire several probes in quick succession.
 */

import { getApiUrl } from '../config/api'

const CACHE_MS = 3000

let inflight = null
let cache = null // { expires: number, value: { ok, status, json } }

export function invalidateUsersMeGetCache() {
  inflight = null
  cache = null
}

async function fetchUsersMe() {
  const response = await fetch(getApiUrl('/api/users/me'), {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' } })
  const json = await response.json().catch(() => ({}))
  return {
    ok: response.ok,
    status: response.status,
    json }
}

/**
 * @returns {Promise<{ ok: boolean, status: number, json: object }>}
 */
export async function getUsersMeCached() {
  const now = Date.now()
  if (cache && cache.expires > now) {
    return cache.value
  }

  if (!inflight) {
    inflight = fetchUsersMe().finally(() => {
      inflight = null
    })
  }

  const value = await inflight

  if (value.ok && value.json?.success) {
    cache = { expires: Date.now() + CACHE_MS, value }
  } else {
    cache = null
  }

  return value
}

/** Refresh cached profile and persist to localStorage (e.g. after creating a listing). */
export async function refreshStoredUserProfile() {
  invalidateUsersMeGetCache()
  const me = await getUsersMeCached()
  if (me.ok && me.json?.data) {
    try {
      localStorage.setItem('user', JSON.stringify(me.json.data))
    } catch {
      /* ignore */
    }
    window.dispatchEvent(new CustomEvent('user-updated'))
    return me.json.data
  }
  return null
}
